import { useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { Checkbox } from '@rocco/components/checkbox';
import { OTHER_OPTION } from '~/modules/business-manager/forms/types';
import type { PublicEnquiryFormField } from '~/modules/enquiry/types/public-enquiry-form';
import {
    isOtherAnswer,
    joinMultiValue,
    splitMultiValue,
} from '~/modules/enquiry/util/custom-field-validation';
import { orderedCountries } from '~/modules/enquiry/util/iso-countries';
import {
    autoComplete,
    inputMode,
    inputType,
} from '~/modules/enquiry/component/CustomFormFields';
import {
    GATE_FIELD_LABEL_CLASS,
    GateFieldError,
    GateFieldLabel,
    GateInput,
    GateTextarea,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { GateSelectField } from './GateSelectField';

export interface EnquiryGateCustomFieldsProps {
    fields: PublicEnquiryFormField[];
    answers: Record<string, string>;
    errors: Record<string, string>;
    disabled?: boolean;
    /** ISO alpha-2 code of where the visitor appears to be; listed first in Country fields. */
    visitorCountry?: string | null;
    /**
     * Lay Half-width questions out in pairs. Off on the sheet, where half of a
     * phone is too narrow for most answers.
     */
    twoColumns: boolean;
    onChange: (key: string, value: string) => void;
}

/**
 * A form's owner-defined questions in the enquiry gate's field design. Answers
 * live in the parent (the configured-enquiry model) so routing prediction and
 * submit see them.
 */
/** Answers too long to share a row: they always take the full width. */
const alwaysFull = (field: PublicEnquiryFormField) =>
    field.width === 'Full' ||
    field.type === 'Textarea' ||
    field.type === 'MultiSelect';

/**
 * Which fields take a full row. Half-width questions sit side by side only in pairs; one
 * without a neighbour to share with takes the whole row rather than leaving half of it empty.
 */
export function fullWidthFieldKeys(
    fields: readonly PublicEnquiryFormField[],
): Set<string> {
    const full = new Set<string>();
    // A half-width question waiting for a neighbour to share its row with.
    let waiting: PublicEnquiryFormField | null = null;
    for (const field of fields) {
        if (alwaysFull(field)) {
            if (waiting) full.add(waiting.key);
            waiting = null;
            full.add(field.key);
        } else if (waiting) {
            waiting = null;
        } else {
            waiting = field;
        }
    }
    if (waiting) full.add(waiting.key);
    return full;
}

export const EnquiryGateCustomFields = ({
    fields,
    answers,
    errors,
    disabled,
    visitorCountry,
    twoColumns,
    onChange,
}: EnquiryGateCustomFieldsProps) => {
    const fullKeys = fullWidthFieldKeys(fields);
    return (
        // Bottom-aligned, so a pair whose labels wrap differently still lines its boxes up.
        <div
            className={cn(
                'grid items-end gap-x-1.5 gap-y-5.5',
                twoColumns ? 'grid-cols-2' : 'grid-cols-1',
            )}
        >
            {fields.map((field) => (
                <div
                    key={field.key}
                    className={cn(
                        twoColumns && fullKeys.has(field.key) && 'col-span-2',
                    )}
                >
                    <GateCustomField
                        field={field}
                        value={answers[field.key] ?? ''}
                        error={errors[field.key]}
                        disabled={disabled}
                        visitorCountry={visitorCountry}
                        onChange={(value) => onChange(field.key, value)}
                    />
                </div>
            ))}
        </div>
    );
};

interface GateCustomFieldProps {
    field: PublicEnquiryFormField;
    value: string;
    error?: string;
    disabled?: boolean;
    visitorCountry?: string | null;
    onChange: (value: string) => void;
}

const fieldLabel = (field: PublicEnquiryFormField) =>
    field.required ? field.label : `${field.label} (optional)`;

const GateCustomField = (props: GateCustomFieldProps) => {
    switch (props.field.type) {
        case 'Select':
        case 'Country':
            return <GateSingleChoice {...props} />;
        case 'MultiSelect':
            return <GateMultiChoice {...props} />;
        default:
            return <GateTextAnswer {...props} />;
    }
};

const GateTextAnswer = ({
    field,
    value,
    error,
    disabled,
    onChange,
}: GateCustomFieldProps) => {
    const inputId = `enquiry-gate-field-${field.key}`;
    const shared = {
        id: inputId,
        'aria-invalid': !!error,
        placeholder: field.placeholder.trim() || field.label,
        value,
        disabled,
    };
    return (
        <div className="flex flex-col gap-1.5">
            <GateFieldLabel htmlFor={inputId}>
                {fieldLabel(field)}
            </GateFieldLabel>
            {field.type === 'Textarea' ? (
                <GateTextarea
                    {...shared}
                    className="h-24"
                    maxLength={2000}
                    onChange={(event) => onChange(event.target.value)}
                />
            ) : (
                <GateInput
                    {...shared}
                    type={inputType(field.type)}
                    inputMode={inputMode(field.type)}
                    autoComplete={autoComplete(field.type)}
                    maxLength={255}
                    onChange={(event) => onChange(event.target.value)}
                />
            )}
            <GateFieldError message={error} />
        </div>
    );
};

/**
 * A dropdown over the owner's options or the country list. With "Other"
 * allowed, an extra choice reveals a text box and the typed text becomes the
 * answer, so the server stores what the visitor wrote rather than "Other".
 */
const GateSingleChoice = ({
    field,
    value,
    error,
    disabled,
    visitorCountry,
    onChange,
}: GateCustomFieldProps) => {
    const isCountry = field.type === 'Country';
    const allowOther = !isCountry && field.allowOther;
    const [otherMode, setOtherMode] = useState(() =>
        isOtherAnswer(field, value),
    );
    // Country answers are stored by name, so the option value is the name too.
    const choices = isCountry
        ? orderedCountries(visitorCountry).map((country) => country.name)
        : field.options;
    const showOtherChoice = allowOther && !choices.includes(OTHER_OPTION);
    const options = [
        ...choices,
        ...(showOtherChoice ? [OTHER_OPTION] : []),
    ].map((choice) => ({ value: choice, label: choice }));

    return (
        <div className="flex flex-col gap-1.5">
            <GateSelectField
                label={fieldLabel(field)}
                placeholder={field.placeholder.trim() || 'Select an option'}
                value={otherMode ? OTHER_OPTION : value}
                options={options}
                error={otherMode ? undefined : error}
                disabled={disabled}
                tall={isCountry}
                onChange={(next) => {
                    const other = showOtherChoice && next === OTHER_OPTION;
                    setOtherMode(other);
                    onChange(other ? '' : next);
                }}
            />
            {otherMode ? (
                <>
                    <GateInput
                        aria-label={`${field.label}: your own answer`}
                        aria-invalid={!!error}
                        placeholder="Please specify"
                        value={value}
                        maxLength={200}
                        disabled={disabled}
                        autoFocus
                        onChange={(event) => onChange(event.target.value)}
                    />
                    <GateFieldError message={error} />
                </>
            ) : null}
        </div>
    );
};

/**
 * Tick-any-that-apply. The answer is one choice per line; with "Other"
 * allowed, the last line may be the visitor's own text.
 */
const GateMultiChoice = ({
    field,
    value,
    error,
    disabled,
    onChange,
}: GateCustomFieldProps) => {
    const chosen = splitMultiValue(value);
    const otherText = chosen.find((c) => !field.options.includes(c)) ?? '';
    const [otherChecked, setOtherChecked] = useState(otherText !== '');
    const showOther = field.allowOther && !field.options.includes(OTHER_OPTION);
    const inputId = `enquiry-gate-field-${field.key}`;

    const emit = (ticked: string[], other: string) =>
        onChange(
            joinMultiValue([
                ...field.options.filter((option) => ticked.includes(option)),
                ...(other.trim() !== '' ? [other.trim()] : []),
            ]),
        );

    return (
        <fieldset
            className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0"
            aria-invalid={!!error}
            disabled={disabled}
        >
            <legend className="mb-1.5 p-0">
                <span className={GATE_FIELD_LABEL_CLASS}>
                    {fieldLabel(field)}
                </span>
            </legend>
            <div className="flex flex-col gap-2.5 rounded-[1rem] bg-[#f6f6f6] px-3 py-3">
                {field.options.map((option, index) => (
                    <GateCheckbox
                        key={option}
                        id={`${inputId}-${index}`}
                        label={option}
                        checked={chosen.includes(option)}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                            emit(
                                checked
                                    ? [...chosen, option]
                                    : chosen.filter((c) => c !== option),
                                otherChecked ? otherText : '',
                            )
                        }
                    />
                ))}
                {showOther ? (
                    <GateCheckbox
                        id={`${inputId}-other`}
                        label={OTHER_OPTION}
                        checked={otherChecked}
                        disabled={disabled}
                        onCheckedChange={(checked) => {
                            setOtherChecked(checked);
                            if (!checked) emit(chosen, '');
                        }}
                    />
                ) : null}
            </div>
            {otherChecked ? (
                <GateInput
                    aria-label={`${field.label}: your own answer`}
                    placeholder="Please specify"
                    value={otherText}
                    maxLength={200}
                    disabled={disabled}
                    autoFocus
                    onChange={(event) => emit(chosen, event.target.value)}
                />
            ) : null}
            <GateFieldError message={error} />
        </fieldset>
    );
};

const GateCheckbox = ({
    id,
    label,
    checked,
    disabled,
    onCheckedChange,
}: {
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onCheckedChange: (checked: boolean) => void;
}) => (
    <div className="flex items-center gap-2.5">
        <Checkbox
            id={id}
            className="size-4.5 rounded-[0.3125rem] border-black/20 bg-white"
            indicatorClassName="size-3.5"
            checked={checked}
            disabled={disabled}
            onCheckedChange={(next) => onCheckedChange(next === true)}
        />
        <label
            htmlFor={id}
            className="cursor-pointer text-[1rem] tracking-[-0.02em] text-[#161616]"
        >
            {label}
        </label>
    </div>
);
